import { FC, memo, useCallback, useMemo } from 'react';

import {
  AlbumIcon,
  ArtistIcon,
  DeleteIcon,
  AddToPlaylist,
  AddToQueueIcon,
  AddToLibrary,
  AddedToLibrary,
  Clock,
} from '../Icons';
import { Dropdown, MenuProps, message } from 'antd';

// Services
import { playerService } from '../../services/player';
import { playlistService } from '../../services/playlists';

// Utils
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { setSleepTimer, isSleepTimerActive } from '../../utils/sleepTimer';

// Interface
import type { Track } from '../../interfaces/track';
import type { Album } from '../../interfaces/albums';
import type { Playlist } from '../../interfaces/playlists';

// Redux
import { fetchQueue } from '../../store/slices/queue';
import { playlistActions } from '../../store/slices/playlist';
import { useAppDispatch, useAppSelector } from '../../store/store';
import {
  fetchMyPlaylists,
  getUserPlaylists,
  yourLibraryActions,
} from '../../store/slices/yourLibrary';
import { api } from '../../store/api';
import { userService } from '../../services/users';
import { likedSongsActions } from '../../store/slices/likedSongs';
import { spotifyActions } from '../../store/slices/spotify';
import { albumActions } from '../../store/slices/album';
import { artistActions } from '../../store/slices/artist';
import { Artist } from '../../interfaces/artist';
import { uiActions } from '../../store/slices/ui';

const ShareIcon = () => (
  <svg role='img' height='16' width='16' viewBox='0 0 16 16' fill='currentColor'>
    <path d='M3 1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V9a.75.75 0 0 0-1.5 0v5H3.5V2.5H8a.75.75 0 0 0 0-1.5H3zm7 0a.75.75 0 0 0 0 1.5h2.94L7.22 8.22a.75.75 0 0 0 1.06 1.06L14 3.56V6.5a.75.75 0 0 0 1.5 0V1a1 1 0 0 0-1-1h-5.5z' />
  </svg>
);

interface TrackActionsWrapperProps {
  canEdit?: boolean;
  saved?: boolean;
  album?: Album | null;
  artist?: Artist | null;
  onSavedToggle?: () => void;
  playlist?: Playlist | null;
  track: Track | Spotify.Track;
  trigger?: ('contextMenu' | 'click')[];
  children: React.ReactNode | React.ReactNode[];
  queueIndex?: number;
  onOpenChange?: (open: boolean) => void;
}

export const TrackActionsWrapper: FC<TrackActionsWrapperProps> = memo((props) => {
  const {
    children,
    artist,
    track,
    playlist,
    canEdit,
    album,
    saved,
    onSavedToggle,
    queueIndex,
    onOpenChange,
  } = props;

  const { t } = useTranslation(['playlist']);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const myPlaylists = useAppSelector(getUserPlaylists);
  const userId = useAppSelector((state) => state.auth.user?.id);
  const currentSong = useAppSelector(
    (state) => state.spotify.state?.track_window?.current_track?.id
  );

  const isLikedInStore = useAppSelector((state) =>
    state.likedSongs.items.some((item) => item.track?.id === track.id) ||
    (state.spotify.state?.track_window?.current_track?.id === track.id && state.spotify.liked)
  );
  const effectiveSaved = saved !== undefined ? saved : isLikedInStore;

  const handleUserValidation = useCallback(
    (button?: boolean) => {
      if (!userId) {
        dispatch(button ? uiActions.openLoginButton() : uiActions.openLoginTooltip());
        return false;
      }
      return true;
    },
    [dispatch, userId]
  );

  const options = useMemo(() => {
    return myPlaylists
      .filter((p) => p.id !== playlist?.id)
      .map((p) => {
        return {
          key: p.id,
          label: p.name,
          onClick: () => {
            if (!handleUserValidation()) return;
            playlistService.addPlaylistItems(p!.id, [track.uri], p?.snapshot_id!, [track]).then(() => {
              dispatch(fetchMyPlaylists());
              dispatch(api.util.invalidateTags([{ type: 'Playlist', id: p!.id }]));
              message.success(t('Added to playlist'));
            });
          },
        };
      });
  }, [myPlaylists, playlist, handleUserValidation, track, dispatch, t]);

  const getItems = () => {
    const items: MenuProps['items'] = [
      {
        label: t('Add to playlist'),
        icon: <AddToPlaylist />,
        key: 'add-to-playlist',
        children: [
          {
            label: t('New playlist'),
            key: 'new-playlist',
            onClick: () => {
              if (!handleUserValidation()) return;
              return playlistService
                .createPlaylist(userId!, { name: track.name })
                .then((response) => {
                  const playlist = response.data;
                  playlistService
                    .addPlaylistItems(playlist.id, [track.uri], playlist.snapshot_id!, [track])
                    .then(() => {
                      dispatch(fetchMyPlaylists());
                      message.success(t('Added to playlist'));
                    });
                });
            },
          },
          { type: 'divider' },
          ...options,
        ],
      },
    ];

    // Save to your Liked Songs / Remove from your Liked Songs
    items.push({
      label: effectiveSaved
        ? t('Remove from your Liked Songs', 'Remove from your Liked Songs')
        : t('Save to your Liked Songs', 'Save to your Liked Songs'),
      key: 'liked-songs',
      icon: effectiveSaved ? (
        <AddedToLibrary style={{ height: 18, width: 18, marginInlineEnd: 0 }} />
      ) : (
        <AddToLibrary style={{ height: 18, width: 18, marginInlineEnd: 0 }} />
      ),
      onClick: () => {
        if (!handleUserValidation(true)) return;
        if (effectiveSaved) {
          userService.deleteTracks([track.id!]).then(() => {
            dispatch(likedSongsActions.removeSong({ id: track.id! }));
            dispatch(albumActions.updateTrackLikeState({ id: track.id!, saved: false }));
            dispatch(artistActions.setTopSongLikeState({ id: track.id!, saved: false }));
            dispatch(playlistActions.setTrackLikeState({ id: track.id!, saved: false }));

            if (currentSong === track.id) {
              dispatch(spotifyActions.setLiked({ liked: false }));
            }

            if (onSavedToggle) onSavedToggle();
            message.success(t('Removed from your Liked Songs', 'Removed from your Liked Songs'));
          });
        } else {
          userService.saveTracks([track.id!]).then(() => {
            if (onSavedToggle) onSavedToggle();

            dispatch(albumActions.updateTrackLikeState({ id: track.id!, saved: true }));
            dispatch(artistActions.setTopSongLikeState({ id: track.id!, saved: true }));
            dispatch(playlistActions.setTrackLikeState({ id: track.id!, saved: true }));

            dispatch(likedSongsActions.fetchLikeSongs());

            if (currentSong === track.id) {
              dispatch(spotifyActions.setLiked({ liked: true }));
            }

            message.success(t('Saved to your Liked Songs', 'Saved to your Liked Songs'));
          });
        }
      },
    });

    // Remove from queue (if item is in upcoming queue)
    if (queueIndex !== undefined && queueIndex >= 0) {
      items.push({
        label: t('Remove from queue', 'Remove from queue'),
        key: 'remove-queue',
        icon: <DeleteIcon />,
        onClick: () => {
          playerService.removeFromQueue(queueIndex);
          message.success(t('Removed from queue', 'Removed from queue'));
        },
      });
    }

    // Add to queue
    items.push({
      label: t('Add to queue'),
      key: 'add-queue',
      icon: <AddToQueueIcon />,
      onClick: () => {
        if (!handleUserValidation(true)) return;
        return playerService.addToQueue(track.uri).then(() => {
          dispatch(fetchQueue());
          message.success(t('Added to queue'));
        });
      },
    });

    if (canEdit && playlist) {
      items.push({
        label: t('Remove from this playlist'),
        key: 'remove-playlist',
        icon: <DeleteIcon />,
        onClick: () => {
          if (!handleUserValidation()) return;
          return playlistService
            .removePlaylistItems(playlist!.id, [track.uri], playlist?.snapshot_id!)
            .then(() => {
              dispatch(api.util.invalidateTags([{ type: 'Playlist', id: playlist!.id }]));
              dispatch(playlistActions.refreshPlaylist(playlist!.id));
              dispatch(playlistActions.removeTrack({ id: track.uri }));
              dispatch(yourLibraryActions.fetchMyPlaylists());
              message.success(t('Removed from playlist'));
            });
        },
      });
    }

    // Sleep timer
    items.push({
      label: t('Sleep timer', 'Sleep timer'),
      key: 'sleep-timer',
      icon: <Clock />,
      children: [
        {
          label: t('5 minutes', '5 minutes'),
          key: 'timer-5',
          onClick: () => setSleepTimer(5),
        },
        {
          label: t('10 minutes', '10 minutes'),
          key: 'timer-10',
          onClick: () => setSleepTimer(10),
        },
        {
          label: t('15 minutes', '15 minutes'),
          key: 'timer-15',
          onClick: () => setSleepTimer(15),
        },
        {
          label: t('30 minutes', '30 minutes'),
          key: 'timer-30',
          onClick: () => setSleepTimer(30),
        },
        {
          label: t('45 minutes', '45 minutes'),
          key: 'timer-45',
          onClick: () => setSleepTimer(45),
        },
        {
          label: t('1 hour', '1 hour'),
          key: 'timer-60',
          onClick: () => setSleepTimer(60),
        },
        {
          label: t('End of track', 'End of track'),
          key: 'timer-end-track',
          onClick: () => setSleepTimer('end_of_track'),
        },
        ...(isSleepTimerActive()
          ? [
              { type: 'divider' as const },
              {
                label: t('Turn off timer', 'Turn off timer'),
                key: 'timer-off',
                onClick: () => setSleepTimer(null),
              },
            ]
          : []),
      ],
    });

    // Go to artist (direct or submenu if multiple artists)
    if (!artist && track.artists && track.artists.length > 0) {
      if (track.artists.length === 1) {
        const a = track.artists[0];
        const artistId = (a as any).id || (a.uri ? a.uri.split(':').reverse()[0] : '');
        items.push({
          label: t('Go to artist'),
          key: 'go-to-artist',
          icon: <ArtistIcon />,
          onClick: () => {
            if (artistId) navigate(`/artist/${artistId}`);
          },
        });
      } else {
        items.push({
          label: t('Go to artist'),
          key: 'go-to-artist-submenu',
          icon: <ArtistIcon />,
          children: track.artists.map((a, idx) => {
            const artistId = (a as any).id || (a.uri ? a.uri.split(':').reverse()[0] : '');
            return {
              label: a.name,
              key: `artist-${artistId || idx}`,
              onClick: () => {
                if (artistId) navigate(`/artist/${artistId}`);
              },
            };
          }),
        });
      }
    }

    // Go to album
    if (!album && track.album) {
      const albumId =
        (track.album as any).id || (track.album.uri ? track.album.uri.split(':').reverse()[0] : '');
      if (albumId) {
        items.push({
          label: t('Go to album'),
          key: 'go-to-album',
          icon: <AlbumIcon />,
          onClick: () => {
            if (!userId) {
              return dispatch(uiActions.openLoginModal(track.album?.images?.[0]?.url || ''));
            }
            navigate(`/album/${albumId}`);
          },
        });
      }
    }

    // Share submenu
    items.push({
      label: t('Share', 'Share'),
      key: 'share',
      icon: <ShareIcon />,
      children: [
        {
          label: t('Copy Song Link', 'Copy Song Link'),
          key: 'copy-link',
          onClick: () => {
            if (track.id) {
              const url = `${window.location.origin}/track/${track.id}`;
              navigator.clipboard.writeText(url);
              message.success(t('Link copied to clipboard', 'Link copied to clipboard'));
            }
          },
        },
      ],
    });

    return items;
  };

  const items = getItems();

  return (
    <>
      <Dropdown menu={{ items }} trigger={props.trigger} onOpenChange={onOpenChange}>
        {children}
      </Dropdown>
    </>
  );
});
